/* eslint-disable no-nested-ternary */
import {
  BlueBirdTheme, Checkbox, Typography,
} from "@kitbird/core";
import {
  Control, Controller, UseFormSetValue,
} from "react-hook-form";
import {
  CardBody, CardContainer, CardHeader, CardSelectAll, CardTitle,
} from "../BusinessCard";
import {
  CustomCheckbox, LayoutContainer, SelectionGroupBody, SelectionGroupContainer,
  SelectionGroupHeader,
} from "../../BusinessTrx.styled";
import {
  IBusinessTrxFormValues, SegmentData, SegmentPoolData, SegmentPools,
} from "../../types";
import BaseLoading from "../../../../../components/Loading/BaseLoading";
import EmptyState from "../EmptyState";
import { useFormBusinessTrx } from "../../FormBusinessTrxProvider";

interface StepDepartmentSelectionProps {
  control: Control<IBusinessTrxFormValues>;
  setValue: UseFormSetValue<IBusinessTrxFormValues>;
}

const StepDepartmentSelection = ({ control, setValue }: StepDepartmentSelectionProps) => {
  const {
    isFetchingDepartment,
    allDepartmentIds,
    rawDepartments,
    resDepartment,
    segments,
    selectedSegmentIds,
    selectedDepartmentIds,
    isEditMode,
  } = useFormBusinessTrx();

  return (
    <CardContainer>
      <CardHeader>
        <CardTitle>
          <Typography level={4} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
            Pilih departemen
          </Typography>
        </CardTitle>
        {!isEditMode && (
          <CardSelectAll>
            <Controller
              name="pilihSemuaDepartemen"
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <Checkbox
                  {...restField}
                  checked={value}
                  onChange={(e) => {
                    const { checked } = e.target;
                    onChange(checked);
                    setValue("selectedDepartmentIds", checked ? allDepartmentIds : []);
                  }}
                  label="Pilih semua departemen"
                  disabled={isFetchingDepartment || allDepartmentIds.length === 0}
                />
              )}
            />
          </CardSelectAll>
        )}
      </CardHeader>
      <CardBody layout="flex" flexDirection="column">
        {isFetchingDepartment ? (
          <BaseLoading />
        ) : resDepartment.length ? (Object.entries(segments)
          .map(([areaName, segmentPools]: [string, SegmentPools]) => (
            Object.entries(segmentPools)
              .map(([poolName, poolData]: [string, SegmentPoolData]) => (
                Object.entries(poolData.companies)
                  .map(([companyCode, segmentList]: [string, SegmentData[]]) => {
                    const companyDepartments = rawDepartments?.list_company
                      .find((c) => c.company_code === companyCode)?.list_department || [];
                    if (companyDepartments.length === 0) return null;
                    const filteredSegments = segmentList.filter((segment: SegmentData) => {
                      const segmentUniqueId = `${poolData.pool_id}.${companyCode}.${segment.segment_code}`;
                      return selectedSegmentIds.includes(segmentUniqueId);
                    });
                    if (filteredSegments.length === 0) return null;
                    return (
                      <div key={`${areaName}-${poolName}-${companyCode}`}>
                        <Typography level={4} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
                          {`Area ${areaName} > Pool ${poolData.pool_id} - ${poolName} > Perusahaan ${companyCode}`}
                        </Typography>
                        <LayoutContainer layout="grid" columns="1fr" marginTop="12px">
                          {filteredSegments.map((segment: SegmentData) => {
                            const allDepartmentIdsInSegment = companyDepartments
                              .map((dep) => `${poolData.pool_id}.${companyCode}.${segment.segment_code}.${dep.department}`);
                            return (
                              <SelectionGroupContainer key={segment.segment_code}>
                                <SelectionGroupHeader>
                                  <Typography level={4} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
                                    {`Pilih departemen untuk segmen ${segment.segment_name}`}
                                  </Typography>
                                </SelectionGroupHeader>
                                <SelectionGroupBody gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))">
                                  {!isEditMode && (
                                    <Checkbox
                                      label="Semua departemen"
                                      checked={allDepartmentIdsInSegment.length > 0
                                        && allDepartmentIdsInSegment
                                          .every((id) => selectedDepartmentIds
                                            .includes(id))}
                                      onChange={(e) => {
                                        const currentSelected = new Set(
                                          selectedDepartmentIds,
                                        );
                                        if (e.target.checked) {
                                          allDepartmentIdsInSegment
                                            .forEach((id) => currentSelected.add(id));
                                        } else {
                                          allDepartmentIdsInSegment
                                            .forEach((id) => currentSelected.delete(id));
                                        }
                                        setValue("selectedDepartmentIds", Array.from(currentSelected));
                                      }}
                                    />
                                  )}
                                  {companyDepartments.map(({ department }) => {
                                    const uniqueValue = `${poolData.pool_id}.${companyCode}.${segment.segment_code}.${department}`;
                                    return (
                                      <Controller
                                        key={uniqueValue}
                                        name="selectedDepartmentIds"
                                        control={control}
                                        defaultValue={[]}
                                        render={({ field: { value, onChange } }) => (
                                          <CustomCheckbox
                                            label={department}
                                            checked={value.includes(uniqueValue)}
                                            onChange={(e) => {
                                              if (isEditMode) {
                                                onChange(e.target.checked ? [uniqueValue] : []);
                                              } else {
                                                const newSelectedIds = new Set(value);
                                                if (e.target.checked) {
                                                  newSelectedIds.add(uniqueValue);
                                                } else {
                                                  newSelectedIds.delete(uniqueValue);
                                                }
                                                onChange(Array.from(newSelectedIds));
                                              }
                                            }}
                                          />
                                        )}
                                      />
                                    );
                                  })}
                                </SelectionGroupBody>
                              </SelectionGroupContainer>
                            );
                          })}
                        </LayoutContainer>
                      </div>
                    );
                  })
              ))
          ))) : (
          <EmptyState />
        )}
      </CardBody>
    </CardContainer>);
};

export default StepDepartmentSelection;
